import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Download, X, Loader2, Share, PlusSquare } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INSTALL_DISMISS_KEY = 'minioto_pwa_install_dismissed_at';
const IOS_INSTALL_DISMISS_KEY = 'minioto_ios_install_dismissed_at';
const PUSH_DISMISS_KEY = 'minioto_push_prompt_dismissed_at_v2';
const INSTALL_DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 gün
const PUSH_DISMISS_MS = 24 * 60 * 60 * 1000; // 1 gün (bildirim daha önemli)

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function isStandalone() {
  return (
    (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true)
  );
}

function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS
  // Only Safari supports "Add to Home Screen"; detect by excluding other iOS browsers
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua);
  return iOS && isSafari;
}

function isDismissed(key, ms) {
  try {
    const v = localStorage.getItem(key);
    if (!v) return false;
    return Date.now() - Number(v) < ms;
  } catch { return false; }
}

export default function PwaPrompt() {
  const { user } = useAuth();
  const [installEvent, setInstallEvent] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showPush, setShowPush] = useState(false);
  const [pushBlocked, setPushBlocked] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [installLoading, setInstallLoading] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [iosExpanded, setIosExpanded] = useState(false);

  // iOS Safari: beforeinstallprompt yok, elle rehber göster
  useEffect(() => {
    if (isStandalone()) return;
    if (!isIos()) return;
    if (isDismissed(IOS_INSTALL_DISMISS_KEY, INSTALL_DISMISS_MS)) return;
    // Give app a beat to settle
    const t = setTimeout(() => setShowIos(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // beforeinstallprompt yakala
  useEffect(() => {
    if (isStandalone()) return;
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      if (!isDismissed(INSTALL_DISMISS_KEY, INSTALL_DISMISS_MS)) setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const onInstalled = () => { setShowInstall(false); setInstallEvent(null); };
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Bildirim durumu kontrolü (giriş yapan kullanıcı için)
  useEffect(() => {
    if (!user) { setShowPush(false); setPushBlocked(false); return; }
    const supported = typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
    if (!supported) return;
    if (isDismissed(PUSH_DISMISS_KEY, PUSH_DISMISS_MS)) return;
    let cancelled = false;
    (async () => {
      try {
        // Gerçek tarayıcı aboneliği var mı? (DB'ye değil, tarayıcıya güven)
        const reg = await navigator.serviceWorker.ready;
        const browserSub = await reg.pushManager.getSubscription();
        if (cancelled) return;
        if (browserSub) return; // zaten aktif abonelik var, sorma
        if (Notification.permission === 'denied') {
          setPushBlocked(true);
          setShowPush(true);
        } else {
          setPushBlocked(false);
          setShowPush(true);
        }
      } catch {
        // serviceWorker hazır değilse yine de teklif et
        if (!cancelled) { setPushBlocked(Notification.permission === 'denied'); setShowPush(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const dismissInstall = () => {
    try { localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now())); } catch {}
    setShowInstall(false);
  };
  const dismissPush = () => {
    try { localStorage.setItem(PUSH_DISMISS_KEY, String(Date.now())); } catch {}
    setShowPush(false);
  };
  const dismissIos = () => {
    try { localStorage.setItem(IOS_INSTALL_DISMISS_KEY, String(Date.now())); } catch {}
    setShowIos(false);
    setIosExpanded(false);
  };

  const handleInstall = async () => {
    if (!installEvent) return;
    setInstallLoading(true);
    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice?.outcome === 'accepted') {
        toast.success('MiniOto uygulaması yüklendi');
      } else {
        toast.message('Yükleme iptal edildi');
        dismissInstall();
      }
      setInstallEvent(null);
      setShowInstall(false);
    } catch {
      toast.error('Yükleme başlatılamadı');
    } finally { setInstallLoading(false); }
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error('Bildirim izni verilmedi');
        dismissPush();
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const r = await axios.get(`${API}/push/vapid-public-key`);
      const key = r.data.public_key;
      if (!key) throw new Error('VAPID anahtarı bulunamadı');
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(key),
        });
      }
      const json = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: json.endpoint,
        keys: json.keys,
        prefs: { replies: true, likes: true, new_topics: true },
      }, { withCredentials: true });
      toast.success('Bildirimler açıldı 🎉');
      setShowPush(false);
      // Çalıştığını görmesi için anında test bildirimi gönder
      axios.post(`${API}/push/test`, {}, { withCredentials: true }).catch(() => {});
    } catch (e) {
      toast.error('Bildirim açılamadı: ' + (e.message || 'bilinmeyen hata'));
    } finally { setPushLoading(false); }
  };

  if (isStandalone() && !showPush) return null;
  if (!showInstall && !showPush && !showIos) return null;

  // Öncelik: iOS kurulum rehberi > Yükleme (Android/Desktop) > Bildirim
  const showingInstall = showInstall && installEvent && !showIos;
  const showingIos = showIos && !showingInstall;

  // iOS kurulum kartı
  if (showingIos) {
    return (
      <div className="fixed bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="pwa-prompt">
        <div className="bg-white border border-emerald-200 shadow-xl rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-xl grid place-items-center bg-emerald-600">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 text-sm">
                MiniOto'yu ana ekrana ekle
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                iPhone'unda uygulama gibi tam ekran kullan.
              </div>
            </div>
            <button
              onClick={dismissIos}
              className="shrink-0 p-1 text-slate-400 hover:text-slate-600"
              aria-label="Kapat"
              data-testid="pwa-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {iosExpanded ? (
            <ol className="mt-3 space-y-2 text-xs text-slate-700">
              <li className="flex items-start gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold shrink-0">1</span>
                <span className="flex-1">
                  Safari'nin alt çubuğundaki <span className="inline-flex items-center gap-1 mx-0.5"><Share className="w-3.5 h-3.5 text-emerald-600 inline" strokeWidth={2.2} /><b>Paylaş</b></span> simgesine dokun.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold shrink-0">2</span>
                <span className="flex-1">
                  Açılan listede <span className="inline-flex items-center gap-1 mx-0.5"><PlusSquare className="w-3.5 h-3.5 text-emerald-600 inline" strokeWidth={2.2} /><b>Ana Ekrana Ekle</b></span> seçeneğine dokun.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold shrink-0">3</span>
                <span className="flex-1">Sağ üstteki <b>Ekle</b>'ye bas. MiniOto ana ekranına eklenir ✅</span>
              </li>
              <li className="text-[11px] text-slate-500 pl-7">
                İpucu: Bildirimler yalnızca ana ekrandan açtığında çalışır.
              </li>
            </ol>
          ) : null}

          <div className="mt-3 flex gap-2">
            {!iosExpanded ? (
              <Button
                size="sm"
                onClick={() => setIosExpanded(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                data-testid="pwa-ios-show-steps"
              >
                Nasıl eklerim?
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={dismissIos}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                data-testid="pwa-ios-done"
              >
                Anladım
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={dismissIos}
              className="h-8 text-slate-500 hover:text-slate-700"
              data-testid="pwa-dismiss-btn"
            >
              Sonra
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="pwa-prompt">
      <div className="bg-white border border-emerald-200 shadow-xl rounded-2xl p-4 flex items-start gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-xl grid place-items-center ${showingInstall ? 'bg-emerald-600' : 'bg-emerald-100'}`}>
          {showingInstall
            ? <Download className="w-5 h-5 text-white" />
            : <Bell className="w-5 h-5 text-emerald-700" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800 text-sm">
            {showingInstall
              ? 'MiniOto uygulamasını yükle'
              : (pushBlocked ? 'Bildirimler engellenmiş' : 'Bildirimleri aç')}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {showingInstall
              ? 'Ana ekrana ekle; forumu uygulama gibi kullan.'
              : (pushBlocked
                  ? 'Tarayıcı adres çubuğundaki kilit simgesine dokun → "Bildirimler" → "İzin ver".'
                  : 'Yanıt ve beğeni geldiğinde anında haberin olsun.')}
          </div>
          <div className="mt-2 flex gap-2">
            {showingInstall ? (
              <Button
                size="sm"
                onClick={handleInstall}
                disabled={installLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                data-testid="pwa-install-btn"
              >
                {installLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Yükle
              </Button>
            ) : pushBlocked ? (
              <Button
                size="sm"
                onClick={dismissPush}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                data-testid="pwa-push-ack-btn"
              >
                Anladım
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleEnablePush}
                disabled={pushLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                data-testid="pwa-push-btn"
              >
                {pushLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />}
                İzin ver
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={showingInstall ? dismissInstall : dismissPush}
              className="h-8 text-slate-500 hover:text-slate-700"
              data-testid="pwa-dismiss-btn"
            >
              Sonra
            </Button>
          </div>
        </div>
        <button
          onClick={showingInstall ? dismissInstall : dismissPush}
          className="shrink-0 p-1 text-slate-400 hover:text-slate-600"
          aria-label="Kapat"
          data-testid="pwa-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
